import { useState, useEffect, useCallback } from 'react';
import { supabase, Wishlist } from '../services/supabase';
import { useAuth } from './useAuth';

export const useWishlists = () => {
  const [wishlists, setWishlists] = useState<Wishlist[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchWishlists = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('wishlists')
        .select(`
          *,
          items:wishlist_items(*)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setWishlists(data || []);
    } catch (error) {
      console.error('Error fetching wishlists:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchWishlists();
    } else {
      setWishlists([]);
      setLoading(false);
    }
  }, [user, fetchWishlists]);

  const createWishlist = async (title: string, description?: string, isPublic = false) => {
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('wishlists')
      .insert({
        user_id: user.id,
        title,
        description,
        is_public: isPublic
      })
      .select()
      .single();

    if (error) throw error;
    
    setWishlists(prev => [data, ...prev]);
    return data;
  };

  const updateWishlist = async (id: string, updates: Partial<Wishlist>) => {
    const { data, error } = await supabase
      .from('wishlists')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    
    setWishlists(prev => 
      prev.map(wishlist => 
        wishlist.id === id ? { ...wishlist, ...data } : wishlist
      )
    );
    return data;
  };

  const deleteWishlist = async (id: string) => {
    const { error } = await supabase
      .from('wishlists')
      .delete()
      .eq('id', id);

    if (error) throw error;
    
    setWishlists(prev => prev.filter(wishlist => wishlist.id !== id));
  };

  const addItemToWishlist = async (
    wishlistId: string, 
    title: string, 
    originalUrl: string,
    price?: number,
    imageUrl?: string
  ) => {
    const { data, error } = await supabase
      .from('wishlist_items')
      .insert({
        wishlist_id: wishlistId,
        title,
        original_url: originalUrl,
        price,
        image_url: imageUrl
      })
      .select()
      .single();

    if (error) throw error;

    // Update local state
    setWishlists(prev => 
      prev.map(wishlist => 
        wishlist.id === wishlistId 
          ? { 
              ...wishlist, 
              items: [...(wishlist.items || []), data] 
            }
          : wishlist
      )
    );

    return data;
  };

  const removeItemFromWishlist = async (itemId: string, wishlistId: string) => {
    const { error } = await supabase
      .from('wishlist_items')
      .delete()
      .eq('id', itemId);

    if (error) throw error;

    // Update local state
    setWishlists(prev => 
      prev.map(wishlist => 
        wishlist.id === wishlistId 
          ? { 
              ...wishlist, 
              items: wishlist.items?.filter(item => item.id !== itemId) || []
            }
          : wishlist
      )
    );
  };

  const markItemAsPurchased = async (itemId: string, wishlistId: string, purchased: boolean) => {
    const { error } = await supabase
      .from('wishlist_items')
      .update({ is_purchased: purchased })
      .eq('id', itemId);

    if (error) throw error;

    // Update local state
    setWishlists(prev => 
      prev.map(wishlist => 
        wishlist.id === wishlistId 
          ? { 
              ...wishlist, 
              items: wishlist.items?.map(item => 
                item.id === itemId ? { ...item, is_purchased: purchased } : item
              ) || []
            }
          : wishlist
      )
    );
  };

  return {
    wishlists,
    loading,
    createWishlist,
    updateWishlist,
    deleteWishlist,
    addItemToWishlist,
    removeItemFromWishlist,
    markItemAsPurchased,
    refetch: fetchWishlists
  };
};
